import React, { useEffect, useState } from 'react';
import { useFinanceStore } from './api/useFinanceStore';
import { PeriodType, CustomPeriod, calculatePeriodRange } from './api/financeApi';
import QuotationManager from './impl/QuotationManager';
import InvoicingHub from './impl/InvoicingHub';
import ExpenseTracker from './impl/ExpenseTracker';
import ReconciliationEngine from './impl/ReconciliationEngine';
import FinancialReports from './impl/FinancialReports';
import IntelligenceDashboard from './impl/IntelligenceDashboard';
import PaymentsView from './impl/PaymentsView';
import BudgetManager from './impl/BudgetManager';

const PERIODS: { value: PeriodType; label: string }[] = [
    { value: 'weekly', label: 'Weekly' },
    { value: 'monthly', label: 'Monthly' },
    { value: 'quarterly', label: 'Quarterly' },
    { value: 'yearly', label: 'Yearly' },
    { value: 'custom', label: 'Custom Range' },
    { value: 'all', label: 'All Periods' },
];

const FinanceView: React.FC = () => {
    const [activeSection, setActiveSection] = useState('intelligence');
    const { setPeriod, period, customPeriod, isLoading, loadProducts } = useFinanceStore();
    const [showCustomDates, setShowCustomDates] = useState(false);
    const [customStart, setCustomStart] = useState('');
    const [customEnd, setCustomEnd] = useState('');

    useEffect(() => {
        setPeriod('monthly');
        loadProducts();
    }, [setPeriod, loadProducts]);

    const handlePeriodChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const value = e.target.value as PeriodType;
        if (value === 'custom') {
            setShowCustomDates(true);
        } else {
            setShowCustomDates(false);
            setPeriod(value);
        }
    };

    const handleCustomApply = () => {
        if (customStart && customEnd) {
            const cp: CustomPeriod = { start: customStart, end: customEnd };
            setPeriod('custom', cp);
            setShowCustomDates(false);
        }
    };

    const periodLabel = calculatePeriodRange(period, new Date(), customPeriod ?? undefined).label;

    const sections = [
        { id: 'intelligence', label: 'Intelligence', component: <IntelligenceDashboard /> },
        { id: 'budget', label: 'Budget', component: <BudgetManager /> },
        { id: 'pipeline', label: 'Quote-to-Cash', component: (
            <div className="grid gap-8">
                <QuotationManager />
                <InvoicingHub />
            </div>
        )},
        { id: 'payments', label: 'Payments', component: <PaymentsView /> },
        { id: 'ops', label: 'Operations', component: (
            <div className="grid gap-8">
                <ExpenseTracker />
                <ReconciliationEngine />
            </div>
        )},
        { id: 'reports', label: 'Reports', component: <FinancialReports /> },
    ];

    return (
        <div className="w-full h-full flex flex-col gap-6">
            <div className="flex flex-col gap-6">
                {/* Header Row: Title and Period Controls */}
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div>
                        <h1 className="text-3xl font-black text-slate-800 uppercase tracking-tighter">Financial Intelligence</h1>
                        <p className="text-slate-400 text-sm font-medium">End-to-end financial pipeline and business intelligence</p>
                    </div>

                    <div className="flex flex-wrap items-center gap-3">
                        <div className="flex items-center gap-2">
                            <select
                                value={period}
                                onChange={handlePeriodChange}
                                className="px-3 py-1.5 rounded-xl text-[10px] font-black uppercase tracking-widest bg-white border border-slate-200 text-slate-700 focus:outline-none focus:border-blue-400 cursor-pointer"
                            >
                                {PERIODS.map(p => (
                                    <option key={p.value} value={p.value}>{p.label}</option>
                                ))}
                            </select>

                            {showCustomDates && (
                                <div className="flex items-center gap-2 animate-in fade-in slide-in-from-left-2 duration-200 bg-white p-1.5 rounded-2xl border border-slate-200 shadow-sm">
                                    <input type="date" value={customStart} onChange={e => setCustomStart(e.target.value)}
                                        className="px-2 py-1 rounded-lg text-xs border border-slate-200 focus:outline-none focus:border-blue-400" />
                                    <span className="text-slate-400 text-xs font-bold">to</span>
                                    <input type="date" value={customEnd} onChange={e => setCustomEnd(e.target.value)}
                                        className="px-2 py-1 rounded-lg text-xs border border-slate-200 focus:outline-none focus:border-blue-400" />
                                    <button onClick={handleCustomApply} disabled={!customStart || !customEnd}
                                        className="px-3 py-1 bg-blue-600 text-white rounded-lg text-[10px] font-black uppercase tracking-widest disabled:opacity-50 hover:bg-blue-700 transition-all">
                                        Apply
                                    </button>
                                </div>
                            )}

                            <div className="px-3 py-1.5 bg-blue-50 text-blue-700 rounded-xl text-[10px] font-black uppercase tracking-widest border border-blue-200">
                                {periodLabel}
                            </div>
                        </div>
                    </div>
                </div>

                {/* Navigation Tab Bar */}
                <div className="flex items-center gap-2 overflow-x-auto pb-2 no-scrollbar">
                    <div className="flex bg-slate-100 p-1 rounded-2xl border border-slate-200 w-fit">
                        {sections.map(s => (
                            <button
                                key={s.id}
                                onClick={() => setActiveSection(s.id)}
                                className={`px-5 py-2.5 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all whitespace-nowrap ${activeSection === s.id ? 'bg-white text-blue-700 shadow-sm ring-1 ring-slate-200' : 'text-slate-500 hover:text-slate-700 hover:bg-slate-200/50'}`}
                            >
                                {s.label}
                            </button>
                        ))}
                    </div>
                </div>
            </div>

            {isLoading ? (
                <div className="flex-1 flex items-center justify-center p-20">
                    <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-600"></div>
                </div>
            ) : (
                <div className="flex-1 animate-in fade-in slide-in-from-bottom-4 duration-500">
                    {sections.find(s => s.id === activeSection)?.component}
                </div>
            )}
        </div>
    );
};

export default FinanceView;
